// ============================================================================
// RAID EDIT MODAL
//
// One modal for all five types, not five modals. In a live workshop a
// "question" routinely turns out to be a risk, and reclassifying must not mean
// deleting and recreating — so type is a segmented control at the top and the
// common fields survive the switch.
//
// Type-specific fields are three small conditional blocks rather than a
// schema-driven renderer: the field sets are genuinely unlike each other and a
// generic renderer for three variants reads worse than the conditionals.
// ============================================================================

import React, { useMemo, useState } from 'react';
import { X, Save, Trash2, Flag, User, CalendarDays, Link2, ShieldAlert } from 'lucide-react';
import {
  RaidItem, RaidType, RaidStatus, RaidLink, RaidOwnerOrg,
  RaidDependencyType, RaidBlockingSeverity,
} from '../../../types/raid';
import {
  RAID_TYPES, RAID_TYPE_ORDER, DEPENDENCY_TYPES, BLOCKING_SEVERITIES, OWNER_ORGS,
  statusLabel, ragFor, ragChip,
} from './raidConfig';
import { RaidLinkPicker, LinkCandidate } from './RaidLinkPicker';
import { RaidResolveInline } from './RaidResolveInline';
import { StringListEditor, SourceField, KeyedValue, toKeyed, fromKeyed } from '../NotesEditor';
import { RaidItemInput } from '../../../services/raid';

export interface RaidEditModalProps {
  /** Omit for create mode. */
  item?: RaidItem | null;
  /** Pre-selected type and links when raising from a card. */
  defaults?: { type?: RaidType; links?: RaidLink[] };
  candidates: LinkCandidate[];
  saving?: boolean;
  onSave: (input: RaidItemInput) => Promise<void> | void;
  onResolve?: (status: RaidStatus, resolution: string) => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
  onClose: () => void;
}

const LABEL = 'block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1';
const INPUT = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';
const SELECT = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white';

export function RaidEditModal({
  item, defaults, candidates, saving, onSave, onResolve, onDelete, onClose,
}: Readonly<RaidEditModalProps>) {
  const createMode = !item;

  const [type, setType] = useState<RaidType>(item?.type ?? defaults?.type ?? 'question');
  const [title, setTitle] = useState(item?.title ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [owner, setOwner] = useState(item?.owner ?? '');
  const [ownerOrg, setOwnerOrg] = useState<RaidOwnerOrg | ''>(item?.ownerOrg ?? '');
  const [dueDate, setDueDate] = useState(item?.dueDate ?? '');
  const [notes, setNotes] = useState<KeyedValue[]>(toKeyed(item?.notes, 'note'));
  const [source, setSource] = useState(item?.source ?? '');
  const [links, setLinks] = useState<RaidLink[]>(item?.links ?? defaults?.links ?? []);

  // Dependency-only
  const [dependencyType, setDependencyType] = useState<RaidDependencyType | ''>(item?.dependencyType ?? '');
  const [blockingSeverity, setBlockingSeverity] = useState<RaidBlockingSeverity | ''>(item?.blockingSeverity ?? '');
  const [targetDate, setTargetDate] = useState(item?.targetDate ?? '');

  // Risk-only
  const [probability, setProbability] = useState<number | ''>(item?.probability ?? '');
  const [impact, setImpact] = useState<number | ''>(item?.impact ?? '');
  const [mitigation, setMitigation] = useState(item?.mitigation ?? '');

  const cfg = RAID_TYPES[type];
  const riskScore = useMemo(
    () => (typeof probability === 'number' && typeof impact === 'number' ? probability * impact : undefined),
    [probability, impact]
  );
  const rag = ragFor(
    typeof probability === 'number' ? probability : undefined,
    typeof impact === 'number' ? impact : undefined
  );

  const buildInput = (): RaidItemInput => ({
    type,
    title: title.trim(),
    description: description.trim() || undefined,
    owner: owner.trim() || undefined,
    ownerOrg: ownerOrg || undefined,
    dueDate: dueDate || undefined,
    notes: fromKeyed(notes),
    source: source.trim() || undefined,
    links,
    // Only send the fields this type owns. The API rejects the others outright
    // rather than stripping them, so a stale value from before a reclassify
    // would fail the whole call.
    ...(type === 'dependency' ? {
      dependencyType: dependencyType || undefined,
      blockingSeverity: blockingSeverity || undefined,
      targetDate: targetDate || undefined,
    } : {}),
    ...(type === 'risk' ? {
      probability: typeof probability === 'number' ? probability : undefined,
      impact: typeof impact === 'number' ? impact : undefined,
      mitigation: mitigation.trim() || undefined,
    } : {}),
  });

  const canSave = title.trim().length > 0 && !saving;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <ShieldAlert size={18} />
            </div>
            <div>
              <h2 className="text-base font-bold text-navy">
                {createMode ? 'Raise a RAID item' : `Edit ${cfg.label.toLowerCase()}`}
              </h2>
              <p className="text-xs text-slate-400">{cfg.hint}</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Type — a segmented control so reclassifying keeps the common fields */}
          <div className="p-5 border-b border-[#e2e8f0]">
            <label className={LABEL}><Flag size={11} />Type</label>
            <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
              {RAID_TYPE_ORDER.map(t => {
                const Icon = RAID_TYPES[t].icon;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 text-xs font-semibold transition-all ${
                      type === t ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    <Icon size={12} />{RAID_TYPES[t].label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Core */}
          <div className="p-5 border-b border-[#e2e8f0] flex flex-col gap-4">
            <div>
              <label className={LABEL} htmlFor="raid-title">Title</label>
              <input
                id="raid-title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={`One line stating the ${cfg.label.toLowerCase()}`}
                className={INPUT}
              />
            </div>
            <div>
              <label className={LABEL} htmlFor="raid-description">Detail</label>
              <textarea
                id="raid-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                placeholder="Background, and what closing this out would take"
                className={`${INPUT} resize-none`}
              />
            </div>
          </div>

          {/* Ownership */}
          <div className="p-5 border-b border-[#e2e8f0] grid grid-cols-3 gap-4">
            <div>
              <label className={LABEL} htmlFor="raid-owner"><User size={11} />Owner</label>
              <input id="raid-owner" value={owner} onChange={e => setOwner(e.target.value)} placeholder="Name" className={INPUT} />
            </div>
            <div>
              <label className={LABEL} htmlFor="raid-org">Organisation</label>
              <select id="raid-org" value={ownerOrg} onChange={e => setOwnerOrg(e.target.value as RaidOwnerOrg | '')} className={SELECT}>
                <option value="">Unassigned</option>
                {OWNER_ORGS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL} htmlFor="raid-due"><CalendarDays size={11} />Due</label>
              <input id="raid-due" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className={SELECT} />
            </div>
          </div>

          {/* Dependency-only */}
          {type === 'dependency' && (
            <div className="p-5 border-b border-[#e2e8f0] grid grid-cols-3 gap-4 bg-amber-50/30">
              <div>
                <label className={LABEL} htmlFor="dep-type">Dependency type</label>
                <select id="dep-type" value={dependencyType} onChange={e => setDependencyType(e.target.value as RaidDependencyType | '')} className={SELECT}>
                  <option value="">Unspecified</option>
                  {DEPENDENCY_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL} htmlFor="dep-sev">Severity</label>
                <select id="dep-sev" value={blockingSeverity} onChange={e => setBlockingSeverity(e.target.value as RaidBlockingSeverity | '')} className={SELECT}>
                  <option value="">Unspecified</option>
                  {BLOCKING_SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                {blockingSeverity && (
                  <p className="mt-1 text-[10px] text-slate-400">
                    {BLOCKING_SEVERITIES.find(s => s.value === blockingSeverity)?.hint}
                  </p>
                )}
              </div>
              <div>
                <label className={LABEL} htmlFor="dep-target">Expected to clear</label>
                <input id="dep-target" type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} className={SELECT} />
              </div>
            </div>
          )}

          {/* Risk-only */}
          {type === 'risk' && (
            <div className="p-5 border-b border-[#e2e8f0] bg-red-50/30 flex flex-col gap-4">
              <div className="grid grid-cols-3 gap-4 items-end">
                <div>
                  <label className={LABEL} htmlFor="risk-prob">Probability (1-5)</label>
                  <select id="risk-prob" value={probability} onChange={e => setProbability(e.target.value ? Number(e.target.value) : '')} className={SELECT}>
                    <option value="">—</option>
                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL} htmlFor="risk-impact">Impact (1-5)</label>
                  <select id="risk-impact" value={impact} onChange={e => setImpact(e.target.value ? Number(e.target.value) : '')} className={SELECT}>
                    <option value="">—</option>
                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Score</label>
                  <div className={`rounded-xl border px-3 py-2 text-sm font-bold text-center ${ragChip(rag)}`}>
                    {riskScore ?? '—'}
                  </div>
                </div>
              </div>
              <div>
                <label className={LABEL} htmlFor="risk-mitigation">Mitigation</label>
                <textarea
                  id="risk-mitigation"
                  value={mitigation}
                  onChange={e => setMitigation(e.target.value)}
                  rows={2}
                  placeholder="What is planned while this risk is still open, as opposed to what happened when it closed"
                  className={`${INPUT} resize-none`}
                />
              </div>
            </div>
          )}

          {/* Links, notes, provenance */}
          <div className="p-5 border-b border-[#e2e8f0] flex flex-col gap-4">
            <RaidLinkPicker candidates={candidates} value={links} onChange={setLinks} />
            <StringListEditor
              label="Notes"
              items={notes}
              onChange={setNotes}
              idPrefix="note"
              addLabel="Add note"
              multiline
              placeholder="Working note"
            />
            <SourceField value={source} onChange={setSource} />
          </div>

          {/* Close out — only once the item exists */}
          {!createMode && onResolve && (
            <div className="p-5">
              {item.status === 'open' ? (
                <RaidResolveInline
                  type={type}
                  currentStatus={item.status}
                  currentResolution={item.resolution}
                  saving={saving}
                  onResolve={onResolve}
                />
              ) : (
                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px] font-semibold shrink-0 mt-0.5">
                    {statusLabel(type, item.status)}
                  </span>
                  <div className="min-w-0">
                    {item.resolution && <p className="text-xs text-slate-600">{item.resolution}</p>}
                    {item.resolvedAt && (
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        Closed {new Date(item.resolvedAt).toLocaleDateString()}{item.resolvedBy ? ` by ${item.resolvedBy}` : ''}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => onResolve('open', '')}
                      className="mt-1 text-[11px] font-semibold text-primary hover:text-primary-dark transition-colors"
                    >
                      Reopen
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
          {onDelete && !createMode ? (
            <button
              type="button"
              onClick={onDelete}
              className="flex items-center gap-1.5 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-500 text-sm font-bold hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
            >
              <Trash2 size={15} /> Delete
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSave(buildInput())}
              disabled={!canSave}
              className="flex items-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-60"
            >
              <Save size={15} /> {createMode ? 'Raise' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
